import { Inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';

import { ElevatedRequestDto } from '@shure/cloud/shared/organizations/data-access';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';

@Injectable({
	providedIn: 'root'
})
export class ElevatedAccessService {
	public readonly elevatedRequest = signal<ElevatedRequestDto | null>(null);
	/**
	 * Signal that reflects whether elevated requests feature is enabled
	 */
	public readonly isElevatedRequestEnabled = toSignal(
		this.featureFlagsService
			.getFeatureFlag$('shure-cloud-rbac-elevatedrequests-enabled')
			.pipe(map((flag: boolean) => !!flag)),
		{ initialValue: false }
	);

	constructor(
		private translocoService: TranslocoService,
		@Inject(FEATURE_FLAG_SERVICE) private featureFlagsService: CloudFeatureFlagsService
	) {}
	/**
	 * set elevated access request detail
	 * @param elevatedRequest - ElevatedRequestDto representation of the received elevated access request
	 */
	public setElevatedRequest(elevatedRequest: ElevatedRequestDto | null): void {
		this.elevatedRequest.set(elevatedRequest);
	}

	/**
	 * gets the stored elevated access request
	 */
	public getElevatedRequest(): ElevatedRequestDto | null {
		return this.elevatedRequest();
	}

	public getTranslatedStrings(i18nKey: string): string {
		switch (i18nKey) {
			case 'hour':
				return this.translocoService.translate('cloud.shared.elevated-access.hour');
			case 'hours':
				return this.translocoService.translate('cloud.shared.elevated-access.hours');
			case 'days':
				return this.translocoService.translate('cloud.shared.elevated-access.days');
			case 'week':
				return this.translocoService.translate('cloud.shared.elevated-access.week');
			case 'bad-request-error':
				return this.translocoService.translate('cloud.shared.elevated-access.bad-request-error');
			case 'internal-server-error':
				return this.translocoService.translate('cloud.shared.error-labels.internal-server-error');
			case 'elevated-request-granted-successfully':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-granted-successfully'
				);
			case 'elevated-request-rejected-successfully':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-rejected-successfully'
				);
			case 'elevated-request-terminated-successfully':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-terminated-successfully'
				);
			case 'elevated-request-extended-successfully':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-extended-successfully'
				);
			case 'elevated-request-revoked-successfully':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-revoked-successfully'
				);
			case 'elevated-request-already-revoked':
				return this.translocoService.translate('cloud.shared.elevated-access.elevated-request-already-revoked');
			case 'elevated-request-already-rejected':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-already-rejected'
				);
			case 'elevated-request-already-accepted':
				return this.translocoService.translate(
					'cloud.shared.elevated-access.elevated-request-already-accepted'
				);
			case 'elevated-request-already-expired':
				return this.translocoService.translate('cloud.shared.elevated-access.elevated-request-already-expired');
			default:
				return '';
		}
	}
}
